import { Cloudinary } from "@cloudinary/url-gen"
import { blur } from "@cloudinary/url-gen/actions/effect"
import { Resize } from "@cloudinary/url-gen/actions/resize"
import Hls from "hls.js"
import { useMemo } from "react"
import {
  CuePoint,
  MyStagePageQuery,
  PictureType,
  PublicationLevel,
  StreamManifestType,
  StreamVariant,
} from "src/generated/graphql"
import {
  AudioDetailPageNode,
  DetailpageData,
  EditorialPlaylistPageNode,
  VideoDetailPageNode,
} from "src/screens/Detail/DetailStore"
import { ccFactor, CloudinaryConf } from "src/utilities/common"
import { create } from "zustand"

import { s } from "../utilities/common"
import { addBreadCrumb, captureSentryException } from "../utilities/sentry"

export const isFavoritedContent = (
  myStage: MyStagePageQuery | undefined,
  content: PlaybackContent | DetailpageData | undefined,
  trackId: string | undefined,
) => {
  if (content?.__typename === "VodConcert") {
    return (
      myStage?.currentUser?.videoFavorites?.edges.some(
        (e) =>
          e.node.content.__typename === "PerformanceWork" &&
          e.node.content.id === trackId,
      ) ?? false
    )
  } else if (content?.__typename === "Album") {
    return (
      myStage?.currentUser?.albumFavorites?.edges.some(
        (e) =>
          e.node.content.__typename === "Album" &&
          e.node.content.id === content.id,
      ) ?? false
    )
  } else if (content?.__typename === "EditorialPlaylist") {
    return (
      myStage?.currentUser?.editorialPlaylistFavorites?.edges.some(
        (e) =>
          e.node.content.__typename === "EditorialPlaylist" &&
          e.node.content.id === content.id,
      ) ?? false
    )
  } else if (content?.__typename === "Video") {
    return (
      myStage?.currentUser?.videoFavorites?.edges.some(
        (e) =>
          e.node.content.__typename === "Video" &&
          e.node.content.id === content.id,
      ) ?? false
    )
  }
  return false
}

// https://bost.ocks.org/mike/shuffle/
export const shuffleArray = <T extends unknown[] | undefined>(array: T): T => {
  if (array === undefined) return array
  const shuffled = [...array]

  const swap = (arr: unknown[], i: number, j: number) => {
    ;[arr[i], arr[j]] = [arr[j], arr[i]]
  }

  const hasOriginalNeighborAt = (arr: unknown[], i: number) => {
    const currentOriginalIndex = array.indexOf(arr[i])

    if (i > 0) {
      const leftOriginalIndex = array.indexOf(arr[i - 1])
      if (Math.abs(currentOriginalIndex - leftOriginalIndex) === 1) return true
    }

    if (i < arr.length - 1) {
      const rightOriginalIndex = array.indexOf(arr[i + 1])
      if (Math.abs(currentOriginalIndex - rightOriginalIndex) === 1) return true
    }

    return false
  }

  const improvesOrFixes = (arr: unknown[], i: number, j: number) => {
    const before =
      Number(hasOriginalNeighborAt(arr, i)) +
      Number(hasOriginalNeighborAt(arr, j))

    swap(arr, i, j)

    const after =
      Number(hasOriginalNeighborAt(arr, i)) +
      Number(hasOriginalNeighborAt(arr, j))

    swap(arr, i, j)

    return after <= before
  }

  // Initial Fisher–Yates shuffle
  let m = shuffled.length
  while (m) {
    const i = Math.floor(Math.random() * m--)
    swap(shuffled, m, i)
  }

  const swappedOnce = new Array(shuffled.length).fill(false)

  const tryRepairPass = () => {
    for (let i = 0; i < shuffled.length; i++) {
      if (!hasOriginalNeighborAt(shuffled, i) || swappedOnce[i]) continue

      const candidates: number[] = []
      for (let j = 0; j < shuffled.length; j++) {
        if (j === i || swappedOnce[j]) continue
        if (improvesOrFixes(shuffled, i, j)) candidates.push(j)
      }

      if (candidates.length > 0) {
        const j = candidates[Math.floor(Math.random() * candidates.length)]
        swap(shuffled, i, j)
        swappedOnce[i] = true
        swappedOnce[j] = true
      }
    }
  }

  tryRepairPass()
  tryRepairPass()

  return shuffled as T
}

export enum PlayerEvent {
  Play = "play",
  Pause = "pause",
  Progress = "progress",
  Loading = "loading",
  Loaded = "loaded",
  Ended = "ended",
}

export type PlaybackContent =
  | VideoDetailPageNode
  | AudioDetailPageNode
  | EditorialPlaylistPageNode
type NavigationProps = {
  open: boolean
  id?: string
  type?: string
  isPreview?: string
  trackId?: string
  startFromProgress?: boolean | number
  startAudioTrack?: string
}

export type Repeat = "none" | "all" | "single"

interface PlayerState {
  navigationProps: NavigationProps
  setNavigationProps: (props: NavigationProps) => void
  returnFocus?: string
  setReturnFocus: (returnFocus?: string) => void
  content?: PlaybackContent
  setContent: (
    content?: PlaybackContent,
    trackId?: string,
    isPreview?: boolean,
  ) => void
  videoRef?: HTMLVideoElement
  setVideoRef: (videoRef: HTMLVideoElement) => void
  subtitles?: string[]
  setSubtitles: (subtitles: string[]) => void
  subtitleSelectOpen: boolean
  setSubtitleSelectOpen: (subtitleSelectOpen: boolean) => void
  trackId?: string
  setTrackId: (trackId?: string) => void
  trackSelectOpen: boolean
  setTrackSelectOpen: (trackSelectOpen: boolean) => void
  streamingQualityOpen: boolean
  setStreamingQualityOpen: (streamingQualityOpen: boolean) => void
  hls?: Hls
  setHls: (hls: Hls) => void
  eventListeners: {
    type: PlayerEvent
    callback: (e: unknown) => void
    uniqueId: string
  }[]
  setEventListener: (
    type: PlayerEvent,
    callback: (e: unknown) => void,
    uniqueId: string,
  ) => void
  removeEventListener: (type: PlayerEvent, uniqueId: string) => void
  duration: number
  setDuration: (duration: number) => void
  isPreview: boolean
  setIsPreview: (isPreview: boolean) => void
  url?: string
  setUrl: (url: string) => void
  resetPlayback: () => void
  poster?: string
  background?: string
  accessToken?: string
  setAccessToken: (accessToken?: string) => void
  publicationLevel?: PublicationLevel
  currentSubTrack: { val: false | string; set: (val: false | string) => void } // needs to have its own setter so the state doesn't update on change
  currentTimeRef: {
    val: number
    set: (val: number) => void
  }
  cuePoints: CuePoint[]
  setCuePoints: (cuePoints: CuePoint[]) => void
  audioTrack?: string
  setAudioTrack: (audioTrack: string) => void
  // this can be useful when defaulting to the old manifest type
  streamingManifestType?: StreamManifestType
  shuffle: boolean
  setShuffle: (shuffle: boolean) => void
  repeat: Repeat
  setRepeat: (repeat: Repeat) => void
  // it's important to keep the shuffled playlist consistent throughout the session
  shuffledPlaylist?: TPlaylistItem[]
}

export interface TPlaylistItem {
  id: string
  index: number
  url?: string
  publicationLevel?: PublicationLevel
  title: string
  subtitle?: string
  subtitleGray?: string
  subtitleWhite?: string
  workDisplayInfo?: string
  album?: { id: string; title: string; picture?: string }
  duration: number
  composer?: string
}

export interface Playlist {
  id: string
  title?: string
  description?: string
  composerTitle?: string
  items: TPlaylistItem[]
}

export const getPlaylist = (
  content?: DetailpageData,
): Playlist[] | undefined => {
  if (content && content.__typename === "VodConcert") {
    return [
      {
        id: content.id,
        items: content.performanceWorks.map((pf, i): TPlaylistItem => {
          // 1. merge artists and groups
          // 2. filter out COMPOSER
          // 3. sort by CONDUCTOR last
          // 4. sort by name
          const artists = [
            ...(pf.artists?.edges ?? []),
            ...(pf.groups?.edges ?? []),
          ]
            .filter(
              (item) =>
                ("role" in item && item.role.type !== "COMPOSER") ||
                !("role" in item),
            )
            .map(
              (item) =>
                `${item.node.name} ${
                  "role" in item
                    ? `(${item.role.displayName})`
                    : `(${item.node.typeDisplayName})`
                }`,
            )
            .join(", ")
          return {
            id: pf.id,
            publicationLevel: content.publicationLevel,
            index: i + 1,
            title: pf.work.title,
            duration: pf.duration,
            subtitle: artists,
            composer: pf.work.composers.map((c) => c.name).join(", "),
          }
        }),
      },
    ]
  }
  if (content && content.__typename === "LiveConcert") {
    return [
      {
        id: content.id,
        items: content.performanceWorks.map(
          (pf, i): TPlaylistItem => ({
            id: pf.id,
            publicationLevel: undefined,
            index: i + 1,
            url: undefined,
            title: pf.work.title,
            duration: pf.duration,
            composer: pf.work.composers.map((c) => c.name).join(", "),
          }),
        ),
      },
    ]
  }
  if (content && content.__typename === "Album") {
    let count = 0
    return content.trackSets.map((trackSet) => ({
      id: content.id + trackSet.trackSetNumber,
      title: trackSet.title ?? "",
      composerTitle: trackSet.composerDisplayInfo,
      items: trackSet.tracks.map(
        (ts): TPlaylistItem => ({
          id: ts.id,
          publicationLevel: PublicationLevel.AccountRequired, //FIXME: find out where to get this prop
          index: ++count,
          title: ts.title,
          subtitle: ts.artistAndGroupDisplayInfo ?? undefined,
          duration: ts.duration,
        }),
      ),
    }))
  }
  if (content && content.__typename === "EditorialPlaylist") {
    let count = 0
    return content.sections.map((trackSet) => ({
      id: content.id + trackSet.title,
      title: trackSet.title ?? "",
      description: trackSet.description,
      // we add it here to make sections visible in the player playlist modal
      composerTitle: trackSet.description,
      items: trackSet.items.map((ts): TPlaylistItem => {
        const imageUrl = ts.album?.pictures?.find(
          (p) => p.type === PictureType.Cover,
        )?.url
        const picture = imageUrl
          ? cld
              .image(imageUrl)
              .resize(Resize.scale().width(s(ccFactor(180), true)))
              .format("jpg")
              .setDeliveryType("fetch")
              .toURL()
          : undefined

        const subtitleAlternative =
          !ts.artistAndGroupDisplayInfo && !ts.composerDisplayInfo
            ? ts.artistsAndGroups
                ?.map((item) => item.name)
                .filter((item) => !!item && item.length > 0)
                .join(", ")
            : undefined
        const subtitleGray = subtitleAlternative ?? ts.artistAndGroupDisplayInfo
        const subtitleWhite = ts.composerDisplayInfo
          ? ts.composerDisplayInfo + (subtitleGray ? ", " : "")
          : undefined
        return {
          id: ts.id,
          workDisplayInfo: ts.workDisplayInfo,
          album: {
            id: ts.album.id,
            picture,
            title: ts.album.title,
          },
          publicationLevel: PublicationLevel.AccountRequired, //FIXME: find out where to get this prop
          index: ++count,
          title: ts.title,
          subtitle: (subtitleWhite ?? "") + (subtitleGray ?? ""),
          subtitleWhite,
          subtitleGray,
          duration: ts.duration,
        }
      }),
    }))
  }
  return undefined
}

export const usePlayList = (
  content?: DetailpageData,
): Playlist[] | undefined => {
  return useMemo(() => getPlaylist(content), [content])
}

const conjugate = (url: string) => (url.includes("?") ? "&" : "?")

export const fetchStreamUrl = async ({
  url,
  accessToken,
  publicationLevel,
}: {
  url?: string
  accessToken?: string
  publicationLevel: PublicationLevel
  isPreview?: boolean
}) => {
  if (!url) return undefined
  const urlWithToken = accessToken
    ? `${url}${conjugate(url)}token=${accessToken}&redirect=false`
    : publicationLevel === "NO_AUTHENTICATION"
      ? `${url}${conjugate(url)}redirect=false`
      : undefined
  if (!urlWithToken) return undefined
  let response
  try {
    response = await fetch(urlWithToken, {
      credentials: "include",
    })
  } catch (e) {
    addBreadCrumb({
      data: {
        fetchRequest: "streamUrl",
        endpoint: url,
      },
    })
    captureSentryException(e)
  }
  if (response) {
    const cdn_url = (await response.json()).cdn_url
    if (!cdn_url) return undefined
    const link = new URL(cdn_url)
    await window.localStorage.setItem(
      "Jwt",
      link.searchParams.get("token") ?? "",
    )
    return link.href
  } else {
    addBreadCrumb({
      data: {
        fetchRequest: "streamUrl",
        endpoint: url,
      },
    })
    captureSentryException(new Error("couldn't fetch stream url"))
  }
  return undefined
}

const preferredManifestOrder = [
  StreamManifestType.HlsCombined,
  StreamManifestType.HlsV2MultichannelVideo,
  StreamManifestType.HlsV2StereoVideo,
  StreamManifestType.HlsV2MultichannelAudioOnly,
  StreamManifestType.HlsV2StereoAudioOnly,
] as StreamManifestType[]

export const filterStreamVariants = (
  streamVariants?: Pick<
    StreamVariant,
    "manifestType" | "url" | "publicationLevel"
  >[],
) => {
  if (!streamVariants) return undefined

  const streamVariantsCopy = [...streamVariants]
    // filter non-hls
    .filter((v) => v.manifestType.includes("HLS"))
    // remove atmos if unsupported
    .filter(
      (v) =>
        window.capabilities.dolbyAtmos ||
        (v.manifestType !== StreamManifestType.HlsV2MultichannelVideo &&
          v.manifestType !== StreamManifestType.HlsV2MultichannelAudioOnly),
    )
    // sort manifests based on preferred variant order
    .sort(
      (a, b) =>
        preferredManifestOrder.indexOf(a.manifestType) -
        preferredManifestOrder.indexOf(b.manifestType),
    )

  return streamVariantsCopy.length > 0 ? streamVariantsCopy[0] : undefined
}

export const getNowPlayingFromContent = async ({
  content,
  trackId,
  isPreview,
  accessToken,
  skipUrlResolve = false,
}: {
  content?: PlaybackContent
  trackId?: string
  isPreview?: boolean
  accessToken?: string
  skipUrlResolve?: boolean
}): Promise<
  | {
      trackId?: string
      url?: string
      publicationLevel: PublicationLevel
      cuePoints: CuePoint[]
      streamManifestType?: StreamManifestType
    }
  | undefined
> => {
  if (!content) return undefined
  if (content.__typename === "VodConcert" && content.performanceWorks) {
    if (!isPreview && trackId && trackId !== "0") {
      const performanceWork = content.performanceWorks.filter(
        (t) => t.id === trackId,
      )[0]
      const variant = filterStreamVariants(performanceWork.streamVariants)
      return {
        url: skipUrlResolve
          ? undefined
          : await fetchStreamUrl({
              url: variant?.url,
              accessToken,
              publicationLevel:
                variant?.publicationLevel ?? content.publicationLevel,
            }),
        trackId,
        publicationLevel: variant?.publicationLevel ?? content.publicationLevel,
        cuePoints: performanceWork.cuePoints,
        streamManifestType: variant?.manifestType,
      }
    }

    const variant =
      isPreview && content.trailerStreamVariants
        ? filterStreamVariants(content.trailerStreamVariants)
        : filterStreamVariants(content.performanceWorks[0].streamVariants)
    return {
      url: skipUrlResolve
        ? undefined
        : await fetchStreamUrl({
            url: variant?.url,
            accessToken,
            publicationLevel:
              variant?.publicationLevel ?? content.publicationLevel,
          }),
      trackId: content.performanceWorks[0].id,
      publicationLevel: content.publicationLevel,
      cuePoints: isPreview ? [] : content.performanceWorks[0].cuePoints,
      streamManifestType: variant?.manifestType,
    }
  }
  if (content.__typename === "Album" && content.trackSets) {
    if (trackId && trackId !== "0") {
      let url, streamManifestType
      let publicationLevel: PublicationLevel = PublicationLevel.TicketRequired
      for (const trackSet of content.trackSets) {
        const ts = trackSet.tracks.filter((t) => t.id === trackId)[0]
        if (!ts) continue
        const variant = filterStreamVariants(ts.streamVariants)
        if (ts) {
          url = skipUrlResolve
            ? undefined
            : await fetchStreamUrl({
                url: variant?.url,
                accessToken,
                publicationLevel:
                  variant?.publicationLevel ?? PublicationLevel.TicketRequired,
              })
          publicationLevel =
            variant?.publicationLevel ?? PublicationLevel.TicketRequired
          streamManifestType = variant?.manifestType
        }
        if (url) break
      }
      return {
        url,
        trackId,
        publicationLevel,
        cuePoints: [],
        streamManifestType,
      }
    }
    const variant = filterStreamVariants(
      content.trackSets[0].tracks[0].streamVariants,
    )
    return {
      url: skipUrlResolve
        ? undefined
        : await fetchStreamUrl({
            url: variant?.url,
            accessToken,
            publicationLevel:
              variant?.publicationLevel ?? PublicationLevel.TicketRequired,
          }),
      trackId: content.trackSets[0].tracks[0].id,
      publicationLevel:
        variant?.publicationLevel ?? PublicationLevel.TicketRequired,
      cuePoints: [],
      streamManifestType: variant?.manifestType,
    }
  }
  if (content.__typename === "EditorialPlaylist" && content.sections) {
    if (trackId && trackId !== "0") {
      let url, streamManifestType
      let publicationLevel: PublicationLevel = PublicationLevel.TicketRequired
      for (const trackSet of content.sections) {
        const ts = trackSet.items.filter((t) => t.id === trackId)[0]
        if (!ts) continue
        const variant = filterStreamVariants(ts.streamVariants)
        if (ts) {
          url = skipUrlResolve
            ? undefined
            : await fetchStreamUrl({
                url: variant?.url,
                accessToken,
                publicationLevel:
                  variant?.publicationLevel ?? PublicationLevel.TicketRequired,
              })
          publicationLevel =
            variant?.publicationLevel ?? PublicationLevel.TicketRequired
          streamManifestType = variant?.manifestType
        }
        if (url) break
      }
      return {
        url,
        trackId,
        publicationLevel,
        cuePoints: [],
        streamManifestType,
      }
    }
    const variant = filterStreamVariants(
      content.sections[0].items[0].streamVariants,
    )
    return {
      url: skipUrlResolve
        ? undefined
        : await fetchStreamUrl({
            url: variant?.url,
            accessToken,
            publicationLevel:
              variant?.publicationLevel ?? PublicationLevel.TicketRequired,
          }),
      trackId: content.sections[0].items[0].id,
      publicationLevel:
        variant?.publicationLevel ?? PublicationLevel.TicketRequired,
      cuePoints: [],
      streamManifestType: variant?.manifestType,
    }
  }
  if (content.__typename === "Video") {
    const variant =
      isPreview && content.trailerStreamVariants
        ? filterStreamVariants(content.trailerStreamVariants)
        : filterStreamVariants(content.streamVariants)
    return {
      url: skipUrlResolve
        ? undefined
        : await fetchStreamUrl({
            url: variant?.url,
            accessToken,
            publicationLevel:
              variant?.publicationLevel ?? PublicationLevel.TicketRequired,
          }),
      publicationLevel: content.publicationLevel,
      cuePoints: [],
      streamManifestType: variant?.manifestType,
    }
  }
  if (content.__typename === "LiveConcert") {
    const variant =
      isPreview && content.trailerStreamVariants
        ? filterStreamVariants(content.trailerStreamVariants)
        : filterStreamVariants(content.streamVariants)
    return {
      url: skipUrlResolve
        ? undefined
        : await fetchStreamUrl({
            url: variant?.url,
            accessToken,
            publicationLevel:
              variant?.publicationLevel ?? PublicationLevel.TicketRequired,
          }),
      publicationLevel: content.publicationLevel,
      cuePoints: [],
      streamManifestType: variant?.manifestType,
    }
    // TODO else display "soon in archive" message
  }
  return undefined
}

const cld = new Cloudinary(CloudinaryConf)

const getPosterForEditorialPlaylistItem = (
  content: PlaybackContent,
  trackId?: string,
) => {
  if (content.__typename === "EditorialPlaylist") {
    const listIndex =
      trackId && trackId !== "0"
        ? (content.sections.findIndex((ts) =>
            ts.items.map((t) => t.id).includes(trackId),
          ) ?? 0)
        : 0
    const trackIndex =
      trackId && trackId !== "0"
        ? content.sections[listIndex]?.items?.findIndex((t) => t.id === trackId)
        : 0
    // set video poster
    const imageUrl = content.sections[listIndex]?.items[
      trackIndex
    ]?.album?.pictures?.find((p) => p.type === "COVER")?.url
    return cld
      .image(imageUrl)
      .format("jpg")
      .resize(Resize.scale().width(s(ccFactor(320), true)))
      .setDeliveryType("fetch")
      .toURL()
  }
}

export const getShuffleOption = () =>
  JSON.parse(localStorage.getItem("shuffle") ?? "false")

export const playerStore = create<PlayerState>((set, getState) => ({
  navigationProps: {
    open: false,
  },
  setNavigationProps: ({
    open,
    id,
    type,
    isPreview,
    trackId,
    startFromProgress,
    startAudioTrack,
  }) => {
    if (!open) {
      set({ content: undefined, eventListeners: [], audioTrack: undefined })
    }
    set({
      navigationProps: {
        open,
        id,
        type,
        isPreview,
        trackId,
        startFromProgress: startFromProgress ?? false,
        startAudioTrack,
      },
    })
  },
  returnFocus: undefined,
  setReturnFocus: (returnFocus?: string) => set({ returnFocus }),
  content: undefined,
  setContent: async (content, trackId, isPreview) => {
    set({ content })
    set({ cuePoints: [] })
    if (!content) {
      set({
        trackId: undefined,
        url: undefined,
        publicationLevel: undefined,
        poster: undefined,
        background: undefined,
        shuffledPlaylist: undefined,
      })
      return
    }
    const nowPlaying = await getNowPlayingFromContent({
      content,
      trackId,
      isPreview,
      accessToken: getState().accessToken,
    })
    const shuffle = getShuffleOption()
    const shuffledPlaylist = shuffleArray(
      getPlaylist(content)?.flatMap((set) => set.items),
    )
    // here comes the tricky part, if the user is on shuffle, but selects a specific track, we need to unshift that
    // track to the beginning of the shuffled list
    if (shuffle && trackId !== "0") {
      const trackIndex = shuffledPlaylist?.findIndex(
        (track) => track.id === trackId,
      )
      if (trackIndex && trackIndex > -1 && shuffledPlaylist) {
        const [track] = shuffledPlaylist.splice(trackIndex, 1)
        shuffledPlaylist.unshift(track)
      }
    }
    set({
      // shuffle if user doesn't select a specific track
      trackId:
        shuffle &&
        trackId === "0" &&
        shuffledPlaylist?.[0] &&
        (content.__typename === "Album" ||
          content.__typename === "EditorialPlaylist")
          ? shuffledPlaylist[0].id
          : nowPlaying?.trackId,
      url: nowPlaying?.url,
      publicationLevel: nowPlaying?.publicationLevel,
      cuePoints: nowPlaying?.cuePoints,
      streamingManifestType: nowPlaying?.streamManifestType,
      shuffledPlaylist,
    })
    if (content.__typename === "Album") {
      // set video poster
      const imageUrl = content.pictures.filter((p) => p.type === "COVER")[0].url
      set({
        poster: cld
          .image(imageUrl)
          .format("jpg")
          .resize(Resize.scale().width(s(ccFactor(340), true)))
          .setDeliveryType("fetch")
          .toURL(),
        background: cld
          .image(imageUrl)
          .format("jpg")
          .resize(Resize.scale().width(s(ccFactor(100), true)))
          .effect(blur().strength(2000))
          .setDeliveryType("fetch")
          .toURL(),
      })
    }
    if (content.__typename === "EditorialPlaylist") {
      set({
        poster: cld
          .image(getPosterForEditorialPlaylistItem(content, trackId))
          .format("jpg")
          .resize(Resize.scale().width(s(ccFactor(340), true)))
          .setDeliveryType("fetch")
          .toURL(),
        background: cld
          .image(getPosterForEditorialPlaylistItem(content, trackId))
          .format("jpg")
          .resize(Resize.scale().width(s(ccFactor(100), true)))
          .effect(blur().strength(2000))
          .setDeliveryType("fetch")
          .toURL(),
      })
    }
  },
  videoRef: undefined,
  setVideoRef: (videoRef) => set({ videoRef }),
  subtitles: undefined,
  setSubtitles: (subtitles) => set({ subtitles }),
  subtitleSelectOpen: false,
  setSubtitleSelectOpen: (subtitleSelectOpen) => set({ subtitleSelectOpen }),
  trackId: undefined,
  setTrackId: async (trackId) => {
    if (trackId !== getState().trackId) {
      set({ duration: 0 })
      const nowPlaying = await getNowPlayingFromContent({
        content: getState().content,
        trackId,
        accessToken: getState().accessToken,
      })
      set({
        trackId,
        url: nowPlaying?.url,
        publicationLevel: nowPlaying?.publicationLevel,
        cuePoints: nowPlaying?.cuePoints ?? [],
        streamingManifestType: nowPlaying?.streamManifestType,
      })
      const content = getState().content
      if (content?.__typename === "EditorialPlaylist") {
        set({
          poster: cld
            .image(getPosterForEditorialPlaylistItem(content, trackId))
            .format("jpg")
            .resize(Resize.scale().width(s(ccFactor(320), true)))
            .setDeliveryType("fetch")
            .toURL(),
          background: cld
            .image(getPosterForEditorialPlaylistItem(content, trackId))
            .format("jpg")
            .resize(Resize.scale().width(s(ccFactor(100), true)))
            .effect(blur().strength(2000))
            .setDeliveryType("fetch")
            .toURL(),
        })
      }
    }
  },
  trackSelectOpen: false,
  setTrackSelectOpen: (trackSelectOpen) => set({ trackSelectOpen }),
  streamingQualityOpen: false,
  setStreamingQualityOpen: (streamingQualityOpen) =>
    set({ streamingQualityOpen }),
  hls: undefined,
  setHls: (hls) => {
    set({ hls })
  },
  eventListeners: [],
  setEventListener: (type, callback, uniqueId) => {
    const el = [...getState().eventListeners]
    const index = getState().eventListeners.findIndex(
      (l) => l.uniqueId === uniqueId && l.type === type,
    )
    if (index === -1) {
      el.push({ type, callback, uniqueId })
      set({ eventListeners: el })
    }
  },
  removeEventListener: (type, uniqueId) => {
    const el = [...getState().eventListeners]
    const index = el.findIndex(
      (l) => l.uniqueId === uniqueId && l.type === type,
    )
    if (index > -1) {
      el.splice(index, 1)
      set({ eventListeners: el })
    }
  },
  duration: 0,
  setDuration: (duration) => set({ duration }),
  isPreview: false,
  setIsPreview: (isPreview) => set({ isPreview }),
  url: undefined,
  setUrl: (url) => set({ url }),
  resetPlayback: () => {
    const startFromProgress = Math.round(getState().videoRef?.currentTime ?? 0)
    getState().hls?.stopLoad()
    getState().hls?.detachMedia()
    set({
      navigationProps: {
        ...getState().navigationProps,
        startFromProgress,
      },
    })
    const url = getState().url
    set({ url: undefined })
    setTimeout(() => set({ url }), 200)
  },
  poster: undefined,
  accessToken: undefined,
  setAccessToken: (accessToken) => set({ accessToken }),
  publicationLevel: undefined,
  currentSubTrack: {
    val: false,
    set: function (val: false | string) {
      this.val = val
    },
  },
  currentTimeRef: {
    val: 0,
    set: function (val: number) {
      this.val = val
    },
  },
  cuePoints: [],
  setCuePoints: (cuePoints) => set({ cuePoints }),
  audioTrack: undefined,
  setAudioTrack: (audioTrack) => set({ audioTrack }),
  shuffle: getShuffleOption(),
  setShuffle: (shuffle: boolean) => {
    localStorage.setItem("shuffle", JSON.stringify(shuffle))
    const content = getState().content
    const playlist = getPlaylist(content)?.flatMap((set) => set.items)
    if (!playlist || !shuffle) {
      set({ shuffle })
      return
    }
    const trackId = getState().trackId
    // hoist the current playing track to the beginning of the shuffled list
    const current = playlist.find((i) => i.id === trackId)
    const rest = shuffleArray(playlist.filter((i) => i.id !== trackId))
    const shuffledPlaylist = current ? [current, ...rest] : [...rest]
    set({
      shuffle,
      shuffledPlaylist,
    })
  },
  repeat: (localStorage.getItem("repeat") ?? "none") as Repeat,
  setRepeat: (repeat: Repeat) => {
    localStorage.setItem("repeat", repeat)
    set({ repeat })
  },
}))
